/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Drawer
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical right-anchored slide-in panel for detail/inspector
 *                  views. Same overlay rules as <Modal> (mode-aware scrim that
 *                  blocks input but keeps the page visible; no outside-close —
 *                  close via ✕ / Escape) + framed, elevated panel. Replaces the
 *                  hand-rolled `fixed inset-y-0 right-0` drawers.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: DrawerWidth;
  className?: string;
}

const widthClass: Record<DrawerWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/** Canonical side drawer. See the design blueprint before building another overlay. */
export const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, title, children, footer, width = 'md', className }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      {/* Scrim: blocks input, page stays visible; stronger in dark. No outside-close. */}
      <div className="fixed inset-0 bg-black/15 dark:bg-black/45" aria-hidden="true" />
      <div
        className={clsx(
          'relative flex h-full w-full flex-col overflow-hidden',
          'border-l-2 border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-[var(--shadow-lg)]',
          widthClass[width],
          className,
        )}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
          <h3 className="text-[length:var(--text-h3)] font-semibold text-[var(--color-text-primary)]">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-6 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default Drawer;
